import { IconApproved } from 'assets/img';
import { PrimaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { useCallback } from 'react';
import { ContextMenuItem, LwButton } from 'redesign';
import { shiftAlerts, useAlert } from 'shared/components/alerts';
import { shiftClaimService as hqShiftClaimService } from 'hq/services';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { shiftClaimService as ogpShiftClaimService } from 'ogp/services';
import { QUERY_KEYS } from 'api/query-keys';
import { usePartnerClaimShift } from 'partner/components/views/partner-shift-planning/partner-shift-planning-modal/hooks/use-partner-claim-shift';

const useGetAddShiftClaimActions = ({ isHq }: { isHq: boolean }) => {
  const { alertError, alertSuccess } = useAlert();
  const { openDialog, disableDialog, enableDialog, closeDialog } = useDialog();
  const queryClient = useQueryClient();
  const getMutation = isHq ? useHQCreateShiftClaim : useOGPCreateShiftClaim;
  const addClaimShift = getMutation({
    onSuccess: () => {
      alertSuccess(shiftAlerts.success.claimed);
      closeDialog();
    },
    onError: (e) => alertError(e),
    onSettled: () => {
      enableDialog();
    },
  });

  const partnerClaimShift = usePartnerClaimShift();

  const getShiftClaimDialogConfig = useCallback(
    (flexWorkerName: string) => ({
      title: `Weet je zeker dat je ${flexWorkerName} aan deze shift wilt toevoegen?`,
      content: 'Let op: de annuleringsvoorwaarden van deze dienst zijn van toepassing.',
      primaryButton: (
        props: Pick<React.ComponentProps<typeof LwButton>, 'disabled'> & { onClick: () => void }
      ) => <PrimaryButton {...props}>Akkoord</PrimaryButton>,
    }),
    []
  );

  const handleFlexWorkerShiftClaimAddition = useCallback(
    (flexWorkerId: string, shiftId: string, flexWorkerName: string) => {
      openDialog(getShiftClaimDialogConfig(flexWorkerName), () => {
        disableDialog();
        addClaimShift.mutate({ flexWorkerId: flexWorkerId, shiftId });
      });
    },
    [addClaimShift, disableDialog, openDialog, getShiftClaimDialogConfig]
  );

  const handlePartnerWorkerShiftClaimAddition = useCallback(
    (flexWorkerId: string, shiftId: string, flexWorkerName: string) => {
      openDialog(getShiftClaimDialogConfig(flexWorkerName), () => {
        disableDialog();
        partnerClaimShift.mutate(
          { flexWorkerId, shiftId },
          {
            onSuccess: () => {
              alertSuccess(shiftAlerts.success.claimed);
              closeDialog();
              queryClient.invalidateQueries(QUERY_KEYS.shifts());
              queryClient.invalidateQueries(QUERY_KEYS.workers());
              queryClient.invalidateQueries(QUERY_KEYS.placements());
            },
            onError: (e) => alertError(e),
            onSettled: () => {
              enableDialog();
            },
          }
        );
      });
    },
    [
      partnerClaimShift,
      disableDialog,
      openDialog,
      alertSuccess,
      alertError,
      closeDialog,
      queryClient,
      getShiftClaimDialogConfig,
    ]
  );

  const getRowActions = useCallback(
    (flexWorkerId: string, shiftId: string, flexWorkerName: string, partnerId?: string | null) => {
      const handleShiftClaimAddition = partnerId
        ? () => handlePartnerWorkerShiftClaimAddition(flexWorkerId, shiftId, flexWorkerName)
        : () => handleFlexWorkerShiftClaimAddition(flexWorkerId, shiftId, flexWorkerName);

      return [
        <ContextMenuItem onClick={handleShiftClaimAddition} key="addClaim">
          <IconApproved />
          Op shift plaatsen
        </ContextMenuItem>,
      ];
    },
    [handleFlexWorkerShiftClaimAddition, handlePartnerWorkerShiftClaimAddition]
  );

  return getRowActions;
};

type ShiftClaimData = { shiftId: string; flexWorkerId: string };
type ShiftClaimMutationOptions = UseMutationOptions<unknown, unknown, ShiftClaimData>;

const useHQCreateShiftClaim = (options?: ShiftClaimMutationOptions) => {
  const mutationFn = async (createShiftClaimData: ShiftClaimData) =>
    await hqShiftClaimService.createShiftClaim(
      createShiftClaimData.shiftId,
      createShiftClaimData.flexWorkerId
    );

  const queryClient = useQueryClient();
  const mutationOptions: ShiftClaimMutationOptions = {
    ...options,
    onSuccess: async (data, variables, context) => {
      queryClient.invalidateQueries(QUERY_KEYS.shifts());
      options && options?.onSuccess?.(data, variables, context);
    },
  };

  return useMutation(mutationFn, mutationOptions);
};

const useOGPCreateShiftClaim = (options?: ShiftClaimMutationOptions) => {
  const mutationFn = async (createShiftClaimData: ShiftClaimData) =>
    await ogpShiftClaimService.createShiftClaim(
      createShiftClaimData.shiftId,
      createShiftClaimData.flexWorkerId
    );

  const queryClient = useQueryClient();
  const mutationOptions: ShiftClaimMutationOptions = {
    ...options,
    onSettled: async (data, error, variables, context) => {
      queryClient.invalidateQueries(QUERY_KEYS.shifts());
      if (options?.onSettled) {
        options.onSettled(data, error, variables, context);
      }
    },
  };

  return useMutation(mutationFn, mutationOptions);
};

export { useGetAddShiftClaimActions };
